<script setup lang="ts">
import { computed } from 'vue';
import {
  ALLERGEN_CODES,
  ALLERGEN_META,
  type IDeclarationLine,
  type TAllergenCode,
  type TAllergenPresence,
} from './regulatory-declarations.types';
import AllergenIcon from './AllergenIcon.vue';
import './epp-shared.css';

type TCellState = 'none' | TAllergenPresence;

const props = defineProps<{
  modelValue: IDeclarationLine[];
  compact?: boolean;
}>();

const emit = defineEmits<{
  'update:modelValue': [lines: IDeclarationLine[]];
}>();

const stateByCode = computed<Record<TAllergenCode, TCellState>>(() => {
  const map = Object.fromEntries(
    ALLERGEN_CODES.map((code) => [code, 'none' as TCellState]),
  ) as Record<TAllergenCode, TCellState>;
  for (const line of props.modelValue) {
    map[line.code] = line.presence;
  }
  return map;
});

function setState(code: TAllergenCode, state: TCellState) {
  const next = props.modelValue.filter((line) => line.code !== code);
  if (state !== 'none') {
    next.push({ code, presence: state });
  }
  emit('update:modelValue', next);
}
</script>

<template>
  <div class="atg__grid" :class="{ 'atg__grid--compact': compact }">
    <div v-for="code in ALLERGEN_CODES" :key="code" class="atg__row">
      <div class="atg__info">
        <div class="atg__ico"><AllergenIcon :code="code" /></div>
        <div class="atg__text">
          <p class="atg__label">{{ ALLERGEN_META[code].label }}</p>
          <p v-if="ALLERGEN_META[code].hint" class="atg__hint">
            {{ ALLERGEN_META[code].hint }}
          </p>
        </div>
      </div>
      <div class="atg__toggle-group">
        <button
          type="button"
          class="atg__opt"
          :class="{ 'atg__opt--active': stateByCode[code] === 'none' }"
          @click="setState(code, 'none')"
        >
          Aucun
        </button>
        <button
          type="button"
          class="atg__opt atg__opt--contains"
          :class="{ 'atg__opt--active': stateByCode[code] === 'contains' }"
          @click="setState(code, 'contains')"
        >
          Contient
        </button>
        <button
          type="button"
          class="atg__opt atg__opt--traces"
          :class="{ 'atg__opt--active': stateByCode[code] === 'may_contain' }"
          @click="setState(code, 'may_contain')"
        >
          Traces
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.atg__grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.875rem;
}
.atg__grid--compact {
  grid-template-columns: 1fr;
  gap: 0.625rem;
}
@media (max-width: 720px) {
  .atg__grid {
    grid-template-columns: 1fr;
  }
}

.atg__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.75rem 0.875rem;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.75rem;
  background: var(--nxt-surface-low);
  flex-wrap: wrap;
}

.atg__info {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  min-width: 0;
}
.atg__ico {
  width: 2rem;
  height: 2rem;
  border-radius: 0.5rem;
  background: var(--nxt-surface-mid);
  border: 1px solid var(--nxt-outline-variant);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8125rem;
  color: var(--nxt-primary);
  flex-shrink: 0;
}
.atg__text {
  min-width: 0;
}
.atg__label {
  margin: 0;
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
}
.atg__hint {
  margin: 0.125rem 0 0;
  font-size: 0.75rem;
  color: var(--nxt-on-surface-muted);
}

.atg__toggle-group {
  display: inline-flex;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.625rem;
  overflow: hidden;
  flex-shrink: 0;
}
.atg__opt {
  height: 1.875rem;
  padding: 0 0.75rem;
  border: none;
  border-right: 1px solid var(--nxt-outline-variant);
  background: var(--nxt-card-bg);
  color: var(--nxt-on-surface-muted);
  font-size: 0.75rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  transition:
    background 0.15s,
    color 0.15s;
}
.atg__opt:last-child {
  border-right: none;
}
.atg__opt--active {
  background: var(--nxt-primary);
  color: #fff;
}
.atg__opt--contains.atg__opt--active {
  background: #ef4444;
}
.atg__opt--traces.atg__opt--active {
  background: #f59e0b;
}

[data-theme='dark'] .atg__row {
  background: rgba(20, 28, 41, 0.8);
  border-color: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .atg__ico {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.08);
  color: #ffd60a;
}
[data-theme='dark'] .atg__opt {
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.6);
}
[data-theme='dark'] .atg__opt--active {
  background: #ffd60a;
  color: #0b1320;
}
[data-theme='dark'] .atg__opt--contains.atg__opt--active {
  background: #ef4444;
  color: #fff;
}
[data-theme='dark'] .atg__opt--traces.atg__opt--active {
  background: #f59e0b;
  color: #0b1320;
}
</style>
